import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrentSubscriptionCard } from './current-subscription-card';
import { BillingHistoryTable } from './billing-history-table';
import { useSubscription, useTopUpSubscription } from '@/hooks/subscription';
import { useQuery } from '@tanstack/react-query';
import { paymentService, type Payment } from '@/services/payment/payment.service';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useCompanyStore } from '@/store/use-company-store';

export function BillingDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentCompanyId } = useCompanyStore();
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscription();
  const topUpMutation = useTopUpSubscription();
  
  const { data: paymentHistory, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['payments', 'history', currentCompanyId],
    queryFn: async () => {
      const response = await paymentService.getPaymentHistory({ limit: 100 }); 
      return response;
    },
    enabled: !!currentCompanyId,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = isLoadingSubscription || isLoadingPayments;

  const handleTopUp = () => {
    if (subscription?.id) {
      navigate(`/billing/checkout?subscriptionId=${subscription.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('billing.title')}</h1>
        <p className="text-sm text-slate-600 mt-1">{t('billing.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Subscription */}
        <div className="lg:col-span-1">
          {subscription && subscription.plan ? (
            <CurrentSubscriptionCard
              subscription={subscription}
              onTopUp={handleTopUp}
              onUpgradePlan={() => navigate('/billing/upgrade')}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t('billing.noActiveSubscription')}</CardTitle>
                <CardDescription>{t('billing.noActiveSubscriptionDescription')}</CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>

        {/* Plan Details */}
        <div className="lg:col-span-1">
          {subscription && subscription.plan ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('billing.planDetails.title')}</CardTitle>
                <CardDescription>{t('billing.planDetails.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">{t('billing.planDetails.planName')}</p>
                  <p className="text-lg font-semibold text-slate-800">{subscription.plan.displayName}</p>
                </div>
                {subscription.plan.description && (
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{t('billing.planDetails.planDescription')}</p>
                    <p className="text-sm text-slate-700">{subscription.plan.description}</p>
                  </div>
                )}
                {subscription.plan.features && (
                  <div>
                    <p className="text-sm text-slate-600 mb-2">{t('billing.planDetails.features')}</p>
                    <ul className="space-y-1">
                      {Object.entries(subscription.plan.features as Record<string, any>).map(([key, value]) => {
                        if (value === null || value === undefined || (key === 'unlimitedUsers' && value === false)) return null;
                        const featureLabel = t(`billing.planDetails.feature.${key}`, key);
                        let displayValue: string;
                        
                        if (key === 'unlimitedUsers') {
                          displayValue = t('billing.plans.unlimitedUsers');
                        } else if (key === 'maxStaffUsers') {
                          displayValue = value === -1 ? t('billing.plans.unlimitedStaffMembers') : `${value} ${t('billing.plans.staffMembers')}`;
                        } else if (key === 'ram') {
                          displayValue = `${value} GB RAM`;
                        } else if (key === 'storage') {
                          displayValue = `${value} GB SSD Storage`;
                        } else {
                          displayValue = value === -1 ? t('billing.plans.unlimitedUsers') : String(value);
                        }
                        
                        return (
                          <li key={key} className="text-sm text-slate-700 flex items-center gap-2">
                            <span className="text-emerald-500">•</span>
                            <span>{featureLabel}: {displayValue}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-600 mb-1">{t('billing.planDetails.price')}</p>
                  <p className="text-2xl font-bold text-slate-800">
                    ${subscription.plan.priceInCents} <span className="text-base font-normal text-slate-500">/{t('billing.month')}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t('billing.planDetails.noPlan')}</CardTitle>
                <CardDescription>{t('billing.planDetails.noPlanDescription')}</CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.history')}</CardTitle>
          <CardDescription>{t('billing.historyDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="p-4">
            <BillingHistoryTable
              payments={
                (paymentHistory && typeof paymentHistory === 'object' && 'data' in paymentHistory && paymentHistory.data) 
                  ? (Array.isArray(paymentHistory.data) ? paymentHistory.data : [])
                  : (Array.isArray(paymentHistory) ? paymentHistory : [])
              }
              isLoading={isLoadingPayments}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

